/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, type ReactNode, type Ref, useCallback, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import { announce } from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { token } from '@atlaskit/tokens';

const activeStyle = css({
	backgroundColor: token('color.background.information'),
	borderRadius: token('radius.xsmall'),
});

const enteredStyle = css({
	outlineOffset: token('space.075'),
	outlineWidth: token('border.width.selected'),
	outlineStyle: 'solid',
	outlineColor: token('color.border.selected'),
});

export type GroupDropIndicatorWithDragOverProps = {
	ariaLabel?: string;
	children: ReactNode;
	disableEntered?: boolean;
	isActive: boolean;
	testId?: string;
};

function GroupDropIndicatorWithDragOverInner(
	{ children, isActive, testId, disableEntered, ariaLabel }: GroupDropIndicatorWithDragOverProps,
	forwardedRef: Ref<HTMLDivElement>,
) {
	const [isEntered, setIsEntered] = React.useState(false);
	const ref = useRef<HTMLDivElement>(null);

	const handleDragEnter = useCallback(() => {
		if (!isActive || Boolean(disableEntered) || isEntered) {
			return;
		}
		setIsEntered(true);
		// Announce directly in the event handler rather than in a chained effect,
		// so the live-region fires in the same event dispatch as the state change.
		if (ariaLabel) {
			announce(ariaLabel);
		}
	}, [disableEntered, isActive, isEntered, ariaLabel]);

	const handleDragLeave = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			if (!isActive || Boolean(disableEntered) || !isEntered) {
				return;
			}
			const relatedTarget = event.relatedTarget as HTMLElement;

			if (ref.current && relatedTarget && ref.current.contains(relatedTarget)) {
				return; // Still within bounds, don't trigger leave
			}
			setIsEntered(false);
		},
		[disableEntered, isActive, isEntered],
	);

	// `isEntered` is only meaningful when `isActive` is true. Compute the effective
	// value during render instead of resetting state in a chained effect, eliminating
	// one extra render cycle whenever `isActive` becomes false.
	const effectiveIsEntered = isActive && isEntered;

	return (
		<div ref={ref}>
			<div
				onDragEnter={handleDragEnter} // eslint-disable-line @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop
				onDragLeave={handleDragLeave} // eslint-disable-line @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop
				ref={forwardedRef}
				data-testid={testId}
				data-is-active={isActive}
				data-is-entered={effectiveIsEntered}
				css={[isActive && activeStyle, effectiveIsEntered && enteredStyle]}
				role={isActive ? 'region' : undefined}
				aria-label={isActive && ariaLabel ? ariaLabel : undefined}
			>
				{children}
			</div>
		</div>
	);
}

export const GroupDropIndicatorWithDragOver: React.FC<
	Omit<GroupDropIndicatorWithDragOverProps & React.RefAttributes<HTMLDivElement>, 'ref'> &
		React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, GroupDropIndicatorWithDragOverProps>(
	GroupDropIndicatorWithDragOverInner,
);
